<template>
	<div>
		<div class="flex xs-12 xs-between mt6">
			<div class="fs4 lh125 fw7 c1 mb2">{{ $t("identities") }}</div>
			<j-button
				type="tertiary"
				v-if="hasActions"
				@click="handleAdd()"
				v-can:or="['writeIndividualIdentities', 'writeCompanyIdentities']"
			>
				<crm-icon name="add" color="c2" size="small" />
				{{ $t("add") }}
			</j-button>
		</div>
		<j-card v-if="!inlineUpdate && !inlineAdd" class="bg5">
			<j-list :list="identities">
				<div class="flex xs-12 p3" slot="list" slot-scope="{ item }">
					<div class="flex xs-6 xs-left xs-middle fw7 c6 lh125">
						{{ item.type }}
					</div>
					<div class="flex xs-4 xs-left xs-middle lh125">{{ item.value }}</div>
					<div v-if="hasActions" class="flex xs-2 xs-right xs-middle">
						<j-button
							type="tertiary"
							@click="handleUpdate(item)"
							v-can:or="['writeIndividualIdentities', 'writeCompanyIdentities']"
						>
							<crm-icon name="edit" color="c6" size="small" />
						</j-button>
						<j-button
							type="tertiary"
							@click="handleDelete(item.id)"
							v-can:or="['writeIndividualIdentities', 'writeCompanyIdentities']"
						>
							<crm-icon name="delete" color="cError" size="small" />
						</j-button>
					</div>
				</div>
			</j-list>
			<div class="bg5 ta2 fs3 p2" v-if="identities.length === 0">
				{{ $t("errors.no_data") }}
			</div>
		</j-card>

		<!-- Add Modal -->
		<core-modal :name="`addIdentity${id}`" :headline="$t('add_identity')">
			<entity-add
				:id="id"
				for="identities"
				:config="addConfig"
				@added="afterAdded"
			/>
		</core-modal>

		<!-- Update Modal -->
		<core-modal :name="`updateIdentity${id}`" :headline="$t('update_identity')">
			<entity-update
				:id="id"
				for="identities"
				:updateId="updateId"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</core-modal>

		<!-- Inline Add -->
		<div v-if="inlineAdd" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("add_identity") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineAdd = false"
				/>
			</div>
			<entity-add
				:id="id"
				size="small"
				fsLabel="fs3"
				for="identities"
				:config="addConfig"
				@added="afterAdded"
			/>
		</div>

		<!-- Inline Update -->
		<div v-if="inlineUpdate" class="bg4 r2 bc7 b1">
			<div class="flex xs-between p2 xs-12">
				<div class="fw7 fs3 c1">{{ $t("update_identity") }}</div>
				<crm-icon
					size="small"
					color="c1"
					name="cross"
					class="cu2"
					@click.native="inlineUpdate = false"
				/>
			</div>
			<entity-update
				:id="id"
				size="small"
				fsLabel="fs3"
				for="identities"
				:updateId="updateId"
				:config="updateConfig"
				@updated="afterUpdated"
			/>
		</div>
	</div>
</template>

<script>
import JCard from "@/components/JCard/"
import JList from "@/components/JList/"
import JButton from "@/components/JButton/"
import CrmIcon from "@/components/CrmIcon/"
import CoreModal from "@/modules/core/components/CoreModal"
import EntityAdd from "./EntityAdd"
import EntityUpdate from "./EntityUpdate"
import api from "@/modules/core/api/"
import { identityTypes } from "@/forms/enums.js"

export default {
	props: {
		id: String,
		identities: Array,
		hasActions: Boolean,
		mode: String
	},

	components: {
		JCard,
		JList,
		JButton,
		CrmIcon,
		CoreModal,
		EntityAdd,
		EntityUpdate
	},

	data() {
		return {
			inlineAdd: false,
			inlineUpdate: false,
			addConfig: [
				{
					componentName: "j-base-select",
					id: "selectType",
					label: this.$t("select_type"),
					options: [
						...identityTypes,
						{
							name: "Others (Please Specify)",
							value: "Others"
						}
					],
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					id: "otherType",
					label: this.$t("type"),
					showFieldWhen: {
						targetValue: "Others",
						targetId: "selectType"
					},
					validate: "required"
				},
				{
					componentName: "j-base-input-text",
					id: "value",
					label: this.$t("value"),
					validate: "required"
				}
			],

			updateId: null,

			updateConfig: null
		}
	},

	methods: {
		async handleDelete(payload) {
			const res = await api.deleteIdentity({
				entityId: this.id,
				id: payload
			})

			// Error
			if (res.errors) {
				this.$store.dispatch("showError", res.errors)
			}

			this.$emit("deleted")
		},

		handleAdd() {
			if (this.mode === "modal") this.$modal.show(`addIdentity${this.id}`)
			if (this.mode === "inline") {
				this.inlineAdd = !this.inlineAdd
				this.inlineUpdate = false
			}
		},

		handleUpdate(payload) {
			// Set updateId which is id of contact, address, identities, fact etc
			this.updateId = payload.id

			// check if type matches one of the contactType names
			const isMatch = identityTypes
				.map(item => item.value)
				.includes(payload.type)

			let selectType = isMatch ? payload.type : "Others"
			let otherType = isMatch ? null : payload.type

			// Initialize updateConfig
			this.updateConfig = [
				{
					componentName: "j-base-select",
					id: "selectType",
					label: this.$t("select_type"),
					options: [
						...identityTypes,
						{
							name: "Others (Please Specify)",
							value: "Others"
						}
					],
					validate: "required",
					value: selectType
				},
				{
					componentName: "j-base-input-text",
					id: "otherType",
					label: this.$t("type"),
					showFieldWhen: {
						targetValue: "Others",
						targetId: "selectType"
					},
					validate: "required",
					value: otherType
				},
				{
					componentName: "j-base-input-text",
					id: "value",
					label: this.$t("value"),
					validate: "required",
					value: payload.value
				}
			]

			// Show update modal
			if (this.mode === "modal") this.$modal.show(`updateIdentity${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = true
		},

		afterAdded() {
			if (this.mode === "modal") this.$modal.hide(`addIdentity${this.id}`)
			if (this.mode === "inline") this.inlineAdd = false
			this.$emit("added")
		},

		afterUpdated() {
			if (this.mode === "modal") this.$modal.hide(`updateIdentity${this.id}`)
			if (this.mode === "inline") this.inlineUpdate = false
			this.$emit("updated")
		}
	}
}
</script>
